<template>
  <div v-if="isFindingEntity">
    <FindingDrawer
      v-if="showDrawer"
      :visible="showDrawer"
      :findingId="id"
      @close="checkDrawerValue(false)"
      @deleted="handleDeleteFinding"
      @edited="handleEditFinding"
    />
  </div>
  <v-navigation-drawer
    v-else
    temporary
    fixed
    class="pt-8 pb-6"
    right
    overlay-opacity="0.7"
    overlay-color="blue-grey darken-3"
    style="z-index: 99"
    width="520"
    :value="showDrawer"
    @input="checkDrawerValue"
  >
    <div class="d-flex flex-column height-inherit px-8">
      <div class="d-flex align-center mb-4">
        <EntityIcon size="24" :entityType="entityType" />
        <p
          v-snip="1"
          class="pa-0 ma-0 ml-2 mr-4 blue-grey--text text--darken-3 subtitle-2"
          v-if="entityType"
        >
          {{ entityName }}
        </p>
        <StatusChip
          showValidationIcons
          class="cursor-default"
          :entity="localContent"
          @click.native="handleClickOnStatus($event)"
        />
      </div>
      <v-divider />
      <p v-snip="3" class="ma-0 h6 text-left flex-shrink-0 mt-4">
        {{ name }}
      </p>
      <div class="flex-grow-1 mt-4 mb-8">
        <RevContent v-if="isRevEntity" :rev="localContent" />
        <RoutineContent v-if="isRoutineEntity" :routine="localContent" />
        <ChallengeContent v-if="isChallengeEntity" :challenge="localContent" />
      </div>
    </div>
    <template v-slot:append>
      <div class="pt-6 px-8">
        <slot name="footer"></slot>
        <Button
          v-if="!footerSlotExist"
          block
          target="_blank"
          :to="route"
          :text="goToEntityText"
          @click="$emit('navigate')"
        />
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import _ from 'lodash'

import StatusChip from '@/components/Core/StatusChip.vue'
import Button from '@/components/UI/Button.vue'
import RevContent from './RevContent.vue'
import RoutineContent from './RoutineContent.vue'
import ChallengeContent from './ChallengeContent.vue'
import FindingDrawer from '@/components/FindingDrawer/FindingDrawer.vue'
import EntityIcon from '@/components/EntityIcon.vue'

import eventHub, { HUB_EVENTS } from '@/lib/event-hub'
import { mapGetters } from 'vuex'

export default {
  name: 'EntityDrawer',

  components: {
    StatusChip,
    Button,
    RevContent,
    RoutineContent,
    ChallengeContent,
    FindingDrawer,
    EntityIcon,
  },

  props: {
    content: {
      type: Object,
    },

    visible: Boolean,
  },

  data() {
    return {
      showDrawer: false,
      localContent: {},
    }
  },

  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.showDrawer = val
      },
    },
    content: {
      immediate: true,
      handler(val) {
        this.localContent = val ?? {}
      },
    },
  },

  async created() {
    eventHub.$on(HUB_EVENTS.ENTITY_DRAWER.UPDATE, this.handleOpenDrawer)
  },

  beforeDestroy() {
    eventHub.$off(HUB_EVENTS.ENTITY_DRAWER.UPDATE, this.handleOpenDrawer)
    this.showDrawer = false
  },

  computed: {
    ...mapGetters(['organization']),

    entityName() {
      const entityType = _.snakeCase(this.entityType)
      const translation = this.$t(`common.rev_type.${entityType}_one`)

      switch (this.entityType) {
        case 'routine':
          return this.$t('routines.routine_one')
        case 'finding':
          return this.$t('routines.finding_one')
        case 'challenge':
          return this.$t('common.challenge_one')
        default:
          return (
            this.organization?.customNamings?.[this.entityType]?.singular ||
            translation ||
            ''
          )
      }
    },

    goToEntityText() {
      switch (this.entityType) {
        case 'routine':
          return `${this.$t('common.go_to')} ${this.$t('routines.routine_one')}`
        case 'finding':
          return `${this.$t('common.go_to')} ${this.$t('routines.finding_one')}`
        case 'challenge':
          return `${this.$t('common.go_to')} ${this.$t('common.challenge_one')}`
        default:
          return `${this.$t('common.go_to')} ${this.$t('rev.rev')}`
      }
    },

    route() {
      const organization = this.localContent?.organization ?? ''

      switch (this.entityType) {
        case this.$constants.exploreEntityTypes.improvementIdea:
          return `/rev/${organization}/${this.id}`
        case this.$constants.exploreEntityTypes.problemSolving:
          return `/problem-solving/${organization}/${this.id}`
        case this.$constants.exploreEntityTypes.quickAction:
          return `/quick-action/${organization}/${this.id}`
        case this.$constants.exploreEntityTypes.routine:
          return `/routine-executions/${this.id}`
        case this.$constants.exploreEntityTypes.finding:
          return
        case this.$constants.exploreEntityTypes.challenge:
          return `/challenge/${organization}/${this.id}`
        default:
          return ''
      }
    },

    isRevEntity() {
      const revsSet = new Set([
        this.$constants.exploreEntityTypes.improvementIdea,
        this.$constants.exploreEntityTypes.problemSolving,
        this.$constants.exploreEntityTypes.quickAction,
      ])
      return revsSet.has(this.entityType)
    },

    isChallengeEntity() {
      return this.entityType === this.$constants.exploreEntityTypes.challenge
    },

    isRoutineEntity() {
      return this.$constants.exploreEntityTypes.routine === this.entityType
    },

    isFindingEntity() {
      return this.$constants.exploreEntityTypes.finding === this.entityType
    },

    footerSlotExist() {
      return !!this.$slots.footer?.length
    },

    entityType() {
      return this.localContent?.entityType ?? this.localContent?.type ?? ''
    },

    name() {
      return this.localContent?.name ?? ''
    },

    id() {
      return this.localContent?.id ?? ''
    },
  },

  methods: {
    handleDeleteFinding() {
      this.$emit('deleted')
      eventHub.$emit(HUB_EVENTS.ENTITY_DRAWER.DELETED)
      this.closeDrawer()
    },

    closeDrawer() {
      this.showDrawer = false
      this.$emit('close')
    },

    handleEditFinding(finding) {
      this.$emit('edited', finding)
      eventHub.$emit(HUB_EVENTS.ENTITY_DRAWER.UPDATED, finding)
    },

    handleOpenDrawer(entity) {
      this.showDrawer = true
      this.localContent = entity
    },

    checkDrawerValue(open) {
      if (open) return
      this.closeDrawer()
    },

    handleClickOnStatus(event) {
      event.stopPropagation()

      this.$emit('clickStatus')
    },
  },
}
</script>

<style scoped lang="scss">
.height-inherit {
  height: inherit !important;
}
</style>
